import { useEffect, useRef, memo } from 'react';

export default memo(function AnimatedBackground() {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d', { alpha: true });
    if (!ctx) return;
    let animId;
    let particles = [];
    let flowLines = [];
    let meshPoints = [];
    let time = 0;
    let lastFrameTime = 0;

    const isMobile = window.innerWidth < 768;
    const dpr = isMobile ? 1 : Math.min(window.devicePixelRatio || 1, 2);
    const targetFPS = isMobile ? 24 : 60;
    const frameInterval = 1000 / targetFPS;

    const resize = () => {
      try {
        const w = window.innerWidth || 1;
        const h = window.innerHeight || 1;
        canvas.width = w * dpr;
        canvas.height = h * dpr;
        canvas.style.width = w + 'px';
        canvas.style.height = h + 'px';
        ctx.scale(dpr, dpr);
      } catch(e) {}
    };
    resize();

    let resizeTimer;
    const debouncedResize = () => { clearTimeout(resizeTimer); resizeTimer = setTimeout(resize, 200); };
    window.addEventListener('resize', debouncedResize);

    const w = window.innerWidth || 1;
    const h = window.innerHeight || 1;

    // Particles — fewer on mobile
    const particleCount = isMobile ? 10 : 50;
    for (let i = 0; i < particleCount; i++) {
      particles.push({
        x: Math.random() * w, y: Math.random() * h,
        vx: (Math.random() - 0.5) * 0.2, vy: (Math.random() - 0.5) * 0.2,
        size: Math.random() * 1.2 + 0.4,
        opacity: Math.random() * 0.15 + 0.03,
        pulse: Math.random() * Math.PI * 2,
      });
    }

    // Flow lines — fewer on mobile
    const lineCount = isMobile ? 2 : 8;
    for (let i = 0; i < lineCount; i++) {
      flowLines.push({
        y: Math.random() * h, speed: Math.random() * 0.4 + 0.15,
        x: -Math.random() * w * 2, length: Math.random() * 250 + 100,
        opacity: Math.random() * 0.04 + 0.01, curve: Math.random() * 0.3 - 0.15,
      });
    }

    // Mesh points — fewer on mobile
    const meshCount = isMobile ? 2 : 4;
    const hues = [185, 348, 270, 200];
    const opacities = [0.03, 0.02, 0.015, 0.02];
    for (let i = 0; i < meshCount; i++) {
      meshPoints.push({
        x: Math.random() * w, y: Math.random() * h,
        vx: (Math.random() - 0.5) * 0.15, vy: (Math.random() - 0.5) * 0.15,
        radius: Math.random() * 200 + 150,
        hue: hues[i], opacity: opacities[i],
      });
    }

    const cw = () => canvas.width / dpr;
    const ch = () => canvas.height / dpr;

    const draw = (timestamp) => {
      animId = requestAnimationFrame(draw);

      // Throttle FPS
      if (timestamp - lastFrameTime < frameInterval) return;
      lastFrameTime = timestamp;

      const W = cw(), H = ch();
      ctx.clearRect(0, 0, W, H);
      time += 0.003;

      // Mesh gradients
      meshPoints.forEach(p => {
        p.x += p.vx; p.y += p.vy;
        if (p.x < -p.radius || p.x > W + p.radius) p.vx *= -1;
        if (p.y < -p.radius || p.y > H + p.radius) p.vy *= -1;
        const grad = ctx.createRadialGradient(p.x, p.y, 0, p.x, p.y, p.radius);
        const osc = Math.sin(time * 2 + p.hue) * 0.005;
        grad.addColorStop(0, `hsla(${p.hue}, 70%, 50%, ${p.opacity + osc})`);
        grad.addColorStop(1, 'transparent');
        ctx.fillStyle = grad;
        ctx.fillRect(p.x - p.radius, p.y - p.radius, p.radius * 2, p.radius * 2);
      });

      // Flow lines
      flowLines.forEach(l => {
        l.x += l.speed;
        if (l.x > W + l.length) { l.x = -l.length; l.y = Math.random() * H; l.curve = Math.random() * 0.3 - 0.15; }
        const grad = ctx.createLinearGradient(l.x, 0, l.x + l.length, 0);
        grad.addColorStop(0, 'transparent');
        grad.addColorStop(0.2, `rgba(0, 240, 255, ${l.opacity})`);
        grad.addColorStop(0.5, `rgba(255, 0, 60, ${l.opacity * 0.8})`);
        grad.addColorStop(0.8, `rgba(0, 240, 255, ${l.opacity * 0.5})`);
        grad.addColorStop(1, 'transparent');
        ctx.beginPath();
        ctx.moveTo(l.x, l.y);
        ctx.bezierCurveTo(l.x + l.length * 0.33, l.y + Math.sin(time + l.y * 0.01) * 15, l.x + l.length * 0.66, l.y + Math.cos(time + l.y * 0.01) * 15, l.x + l.length, l.y + l.curve * 20);
        ctx.strokeStyle = grad;
        ctx.lineWidth = 1;
        ctx.stroke();
      });

      // Particles
      particles.forEach(p => {
        p.x += p.vx; p.y += p.vy; p.pulse += 0.015;
        if (p.x < 0) p.x = W; if (p.x > W) p.x = 0;
        if (p.y < 0) p.y = H; if (p.y > H) p.y = 0;
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(0, 240, 255, ${p.opacity * (0.6 + Math.sin(p.pulse) * 0.4)})`;
        ctx.fill();
      });

      // Particle connections — desktop only
      if (!isMobile) {
        for (let i = 0; i < particles.length; i++) {
          for (let j = i + 1; j < particles.length; j++) {
            const dx = particles[i].x - particles[j].x;
            const dy = particles[i].y - particles[j].y;
            const dist = dx * dx + dy * dy; // skip sqrt
            if (dist < 14400) { // 120^2
              const op = (1 - Math.sqrt(dist) / 120) * 0.04;
              ctx.beginPath();
              ctx.moveTo(particles[i].x, particles[i].y);
              ctx.lineTo(particles[j].x, particles[j].y);
              ctx.strokeStyle = `rgba(0, 240, 255, ${op})`;
              ctx.lineWidth = 0.5;
              ctx.stroke();
            }
          }
        }
      }
    };

    animId = requestAnimationFrame(draw);
    return () => { cancelAnimationFrame(animId); window.removeEventListener('resize', debouncedResize); clearTimeout(resizeTimer); };
  }, []);

  return <canvas ref={canvasRef} className="fixed inset-0 z-0 pointer-events-none" style={{ opacity: 0.8, willChange: 'auto' }} />;
});
